import { useState, type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { SbSharedCreatorsResultContainer } from '../SbSharedCreatorsResultContainer/SbSharedCreatorsResultContainer';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { useFragment, graphql } from 'react-relay';
import { type SbSharedCategoryTabs_viewer$key } from './__generated__/SbSharedCategoryTabs_viewer.graphql';
import {
    type SbSharedCategoryTabs_creatorRootPage$key,
    type VerticalType,
} from './__generated__/SbSharedCategoryTabs_creatorRootPage.graphql';
import { type SbSharedCategoryTabs_itemSearch$key } from './__generated__/SbSharedCategoryTabs_itemSearch.graphql';

const getVertical = (vertical: VerticalType): ReactNode => {
    switch (vertical) {
        case 'furniture':
            return (
                <FormattedMessage
                    id="sb.SbSharedCategoryTabs.verticalFurniture"
                    defaultMessage="Furniture"
                />
            );
        case 'jewelry':
            return (
                <FormattedMessage
                    id="sb.SbSharedCategoryTabs.verticalJewelry"
                    defaultMessage="Jewelry"
                />
            );
        case 'fashion':
            return (
                <FormattedMessage
                    id="sb.SbSharedCategoryTabs.verticalFashion"
                    defaultMessage="Fashion"
                />
            );
        case 'art':
        default:
            return (
                <FormattedMessage id="sb.SbSharedCategoryTabs.verticalArt" defaultMessage="Art" />
            );
    }
};

type Props = {
    viewer: SbSharedCategoryTabs_viewer$key;
    creatorRootPage: SbSharedCategoryTabs_creatorRootPage$key;
    itemSearch: SbSharedCategoryTabs_itemSearch$key;
};

export const SbSharedCategoryTabs: FC<Props> = ({
    viewer: viewerRef,
    creatorRootPage: creatorRootPageRef,
    itemSearch: itemSearchRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SbSharedCategoryTabs_viewer on Viewer {
                ...SbSharedCreatorsResultContainer_viewer
            }
        `,
        viewerRef
    );
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedCategoryTabs_itemSearch on ItemSearchQueryConnection {
                ...SbSharedCreatorsResultContainer_itemSearch
            }
        `,
        itemSearchRef
    );

    const creatorRootPage = useFragment(
        graphql`
            fragment SbSharedCategoryTabs_creatorRootPage on CreatorRootPage {
                verticals
                verticalsUriRef
                verticalItems {
                    list {
                        ...SbSharedCreatorsResultContainer_items
                    }
                }
            }
        `,
        creatorRootPageRef
    );

    const tabOrder = (creatorRootPage?.verticals || []).filter(filterNulls);
    const [activeTab, setActiveTab] = useState(tabOrder?.[0]);

    const tabs = tabOrder.reduce<{ [key in VerticalType]?: ReactNode }>(
        (acc, tab, idx) => ({
            ...acc,
            [tab]: (
                <SbSharedCreatorsResultContainer
                    viewer={viewer}
                    items={creatorRootPage?.verticalItems?.[idx]?.list?.filter(filterNulls)}
                    itemSearch={itemSearch}
                    vertical={getVertical(tab)}
                    link={creatorRootPage?.verticalsUriRef?.[idx] ?? ''}
                />
            ),
        }),
        {}
    );

    return Object.keys(tabs).length > 1 ? (
        <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
            <TabList indicatorPadding="small" fullWidth>
                {tabOrder.map(name => [name, getVertical(name)])}
            </TabList>
            <TabPanels>{tabOrder.map(name => [name, tabs[name]])}</TabPanels>
        </TabProvider>
    ) : (
        <div>{tabs[tabOrder[0]]}</div>
    );
};
